import { api } from './api';

export type PilotCaps = {
  max_zones: number;
  max_policies: number;
  max_policies_per_camera: number;
};

export type Site = {
  id: string;
  site_id: string;
  org_id: string;
  display_name: string;
  slug?: string | null;
  timezone?: string | null;
  config?: Record<string, any> | null;
  is_default?: boolean;
};

export type SiteComplexity = {
  ok: boolean;
  site_id: string;
  org_id: string;
  timezone?: string | null;
  pilot_mode: boolean;
  pilot_caps: PilotCaps;
  counts: {
    zones: number;
    policies: number;
    unknown_scope_policies: number;
    max_policies_per_camera: number;
  };
  complexity_score: number;
  warnings: Array<{ kind: string; detail?: string; recommended?: string }>;
};

export type SiteArea = {
  area_id: string;
  display_name?: string | null;
  space_type?: string | null;
  active?: boolean | null;
};

export type CameraPlacement = {
  camera_id: string;
  device_id?: string | null;
  area_id?: string | null;
  area_name?: string | null;
  mount_label?: string | null;
  path?: string | null;
  site_display_name?: string | null;
  revision?: number | null;
};

export const sitesApi = {
  async listSites(orgId: string): Promise<Site[]> {
    const res = await api.get('/api/v1/sites', { params: { org_id: orgId } });
    return Array.isArray(res.data?.sites) ? (res.data.sites as Site[]) : [];
  },

  async listAreas(siteId: string): Promise<SiteArea[]> {
    const res = await api.get(`/api/v1/sites/${encodeURIComponent(siteId)}/areas`);
    return Array.isArray(res.data?.areas) ? (res.data.areas as SiteArea[]) : [];
  },

  async listCameraPlacements(siteId: string): Promise<{
    site_display_name: string;
    placements: CameraPlacement[];
  }> {
    const res = await api.get(
      `/api/v1/sites/${encodeURIComponent(siteId)}/camera-placements`,
    );
    return {
      site_display_name: String(res.data?.site_display_name || '').trim(),
      placements: Array.isArray(res.data?.placements)
        ? (res.data.placements as CameraPlacement[])
        : [],
    };
  },

  async getDefaultSite(): Promise<Site> {
    const res = await api.get('/api/v1/sites/default');
    const site = (res.data?.site || {}) as Site;
    return site;
  },

  async getSite(site_id: string): Promise<Site> {
    const res = await api.get(`/api/v1/sites/${encodeURIComponent(site_id)}`);
    const site = (res.data?.site || {}) as Site;
    return site;
  },

  async updateSite(site_id: string, patch: Partial<Pick<Site, 'display_name' | 'slug' | 'timezone' | 'config' | 'is_default'>>): Promise<Site> {
    const res = await api.put(`/api/v1/sites/${encodeURIComponent(site_id)}`, patch);
    const site = (res.data?.site || {}) as Site;
    return site;
  },

  async getComplexity(site_id: string): Promise<SiteComplexity> {
    const res = await api.get(`/api/v1/sites/${encodeURIComponent(site_id)}/complexity`);
    return res.data as SiteComplexity;
  },

  async putDevicePlacement(
    siteId: string,
    deviceId: string,
    body: {
      area_id?: string | null;
      placement_label?: string | null;
      install_notes?: Record<string, unknown> | null;
      effective_at?: string | null;
    },
  ): Promise<{ ok: boolean; placement: Record<string, unknown> }> {
    const res = await api.put(
      `/api/v1/sites/${encodeURIComponent(siteId)}/devices/${encodeURIComponent(deviceId)}/placement`,
      body,
    );
    return res.data as { ok: boolean; placement: Record<string, unknown> };
  },

  async putCameraPlacement(
    siteId: string,
    cameraId: string,
    body: {
      area_id: string;
      mount_label?: string | null;
      device_id?: string | null;
      effective_at?: string | null;
      floor_plan?: Record<string, unknown> | null;
    },
  ): Promise<{ ok: boolean; placement: Record<string, unknown>; placement_revision: number }> {
    const res = await api.put(
      `/api/v1/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/placement`,
      body,
    );
    return res.data as {
      ok: boolean;
      placement: Record<string, unknown>;
      placement_revision: number;
    };
  },

  async createArea(
    siteId: string,
    body: {
      area_id: string;
      display_name?: string;
      space_type?: string | null;
      floor_level?: number | null;
    },
  ): Promise<SiteArea> {
    const res = await api.post(
      `/api/v1/sites/${encodeURIComponent(siteId)}/areas`,
      body,
    );
    return (res.data?.area || res.data) as SiteArea;
  },

  async updateArea(
    siteId: string,
    areaId: string,
    patch: {
      display_name?: string;
      space_type?: string | null;
      floor_level?: number | null;
      active?: boolean;
    },
  ): Promise<SiteArea> {
    const res = await api.put(
      `/api/v1/sites/${encodeURIComponent(siteId)}/areas/${encodeURIComponent(areaId)}`,
      patch,
    );
    return (res.data?.area || res.data) as SiteArea;
  },

  async deleteArea(siteId: string, areaId: string): Promise<{ ok: boolean; deleted: string }> {
    const res = await api.delete(
      `/api/v1/sites/${encodeURIComponent(siteId)}/areas/${encodeURIComponent(areaId)}`,
    );
    return res.data as { ok: boolean; deleted: string };
  },
};
